import { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Checkbox,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  Stack,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
  Divider,
  IconButton,
  Chip,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  ListSubheader,
  ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import BuildIcon from '@mui/icons-material/Build';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';

// Hooks & Components
import { useTireBatchListRepairScrap } from '../../../../stores/tireBatchStore';
import { useCompanyList } from '../../../../stores/companyStore';
import { useTireDamageDataList } from '../../../../stores/tireDamageStore';
import TabWorkOrder from './TabWorkOrder';
import DetailTireDamage from './DetailTireDamage';
import TabTireRepair from './TabTireRepair';
import TabTireScrap from './TabTireScrap';
// import TabScrappedTires from './TabScrappedTires';
import { useLanguage } from '../../../../utils/i18n';

// Export Libs
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      <Box sx={{ pt: 3, display: value === index ? 'block' : 'none' }}>{children}</Box>
    </div>
  );
}

export default function TireRepairScrapPage() {
  const { t } = useLanguage();
  const [tabValue, setTabValue] = useState(0);

  // Pagination & Search State
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Debounced or Enter-triggered

  // Selection State
  const [selectedTires, setSelectedTires] = useState([]);

  // Modals State
  const [detailModal, setDetailModal] = useState({ open: false, data: null });
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [scrapModalOpen, setScrapModalOpen] = useState(false);

  // Work Order Count
  const [woCount, setWoCount] = useState(0);
  // Scrapped Tires Count
  // const [scrappedCount, setScrappedCount] = useState(0);

  // Company Filter State
  const [selectedCompanyValues, setSelectedCompanyValues] = useState(() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && (parsed.company || parsed.company_id)) {
          return [String(parsed.company || parsed.company_id)];
        }
      }
      const rawCompany = localStorage.getItem('companyId');
      if (rawCompany) return [String(rawCompany)];
    } catch (e) {
      console.log(e);
    }
    return [];
  });

  const [companySearchTerm, setCompanySearchTerm] = useState('');

  // Company List from API
  const { companyOptions = [] } = useCompanyList();

  const { data: damageList } = useTireDamageDataList(0, 1000);
  const getInfoTireDamage = (id) => {
    if (!id || !damageList) return null;
    return damageList.find((d) => String(d.id) === String(id));
  };

  const filteredCompanyOptions = (companyOptions || []).filter((opt) => {
    if (!companySearchTerm) return true;
    const label = String(opt.label ?? opt.name ?? opt.value).toLowerCase();
    return label.includes(companySearchTerm.toLowerCase());
  });

  const handleCompanyChange = (e) => {
    const val = e.target.value;
    if (val.includes('all')) {
      if (selectedCompanyValues.length === (companyOptions || []).length) {
        setSelectedCompanyValues([]);
      } else {
        setSelectedCompanyValues((companyOptions || []).map((o) => String(o.value)));
      }
    } else {
      setSelectedCompanyValues(typeof val === 'string' ? val.split(',') : val);
    }
    setPage(1);
  };

  const renderCompanyValue = (selected) => {
    if (selected.length === 0) return t('tireRepairScrap.selectCompany', 'Select Company');
    if (companyOptions && selected.length === companyOptions.length) return t('tireRepairScrap.allCompany', 'All Company');
    const selectedObjs = (companyOptions || [])
      .filter((o) => selected.includes(String(o.value)))
      .map((o) => o.label ?? o.name ?? String(o.value));

    if (selectedObjs.length > 2) {
      return `${selectedObjs.slice(0, 2).join(', ')} + ${selectedObjs.length - 2} more`;
    }
    return selectedObjs.join(', ');
  };

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch Data
  const { data, totalRecord, isLoading, refetch } = useTireBatchListRepairScrap(
    page - 1,
    rowsPerPage,
    selectedCompanyValues.map((val) => ({ value: val })),
    searchQuery ? 'serial_number' : null,
    searchQuery,
    [{ field: 'status_repair', operator: 'isNull', value: '' }]
  );

  const filteredData = (data || []).filter(
    (tire) => !tire.status_repair || tire.status_repair.toUpperCase() === 'CANCELLED' || tire.status_repair.toUpperCase() === 'COMPLETED'
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setSearchQuery(searchInput);
      setPage(1);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTires(filteredData);
    } else {
      setSelectedTires([]);
    }
  };

  const handleSelectOne = (tire) => {
    setSelectedTires((prev) => {
      const isSelected = prev.find((t) => t.id === tire.id);
      if (isSelected) {
        return prev.filter((t) => t.id !== tire.id);
      }
      return [...prev, tire];
    });
  };

  const getWaitingTime = (removeDate) => {
    if (!removeDate) return '-';
    const now = dayjs();
    const removed = dayjs(removeDate);

    const diffInDays = now.diff(removed, 'day');
    if (diffInDays < 1) return 'Today';

    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
  };

  // Export Logic
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const formatExportData = (exportData) => {
    return exportData.map((row) => ({
      [t('tireRepairScrap.serialNumberExport', 'Serial Number')]: row.serial_number || '-',
      [t('tireRepairScrap.priority', 'Priority')]: row.status_backlog || '-',
      [t('tireRepairScrap.damageTypeExport', 'Damage Type')]: row.last_inspection?.tire_damage_name || '-',
      [t('tireRepairScrap.brandExport', 'Brand')]: row.join?.tire_item_tire_brand_name || '-',
      [t('tireRepairScrap.sizeExport', 'Size')]: row.join?.tire_item_tire_size_code_internal || row.join?.tire_item_tire_size_code || '-',
      [t('tireRepairScrap.specPatternExport', 'Spec/Pattern')]: row.join?.m_tire_spec_pattern || row.m_tire_spec_pattern || '-',
      [t('tireRepairScrap.inspectorExport', 'Inspector')]: row.last_inspection?.inspector_name || '-',
      [t('tireRepairScrap.waitingTimeExport', 'Waiting Time')]: getWaitingTime(row.remove_date)
    }));
  };

  const exportToCSV = () => {
    handleExportClose();
    const formattedData = formatExportData(filteredData);
    const csvData = Papa.unparse(formattedData);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `tire-repair-scrap-${dayjs().format('YYYYMMDD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = async () => {
    handleExportClose();
    const formattedData = formatExportData(filteredData);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Repair Scrap');

    if (formattedData.length > 0) {
      const headers = Object.keys(formattedData[0]);
      worksheet.columns = headers.map((h) => ({ header: h, key: h, width: 20 }));
      formattedData.forEach((row) => worksheet.addRow(row));

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };

      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `tire-repair-scrap-${dayjs().format('YYYYMMDD')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    handleExportClose();
    const formattedData = formatExportData(filteredData);
    const doc = new jsPDF('landscape');
    if (formattedData.length > 0) {
      const headers = Object.keys(formattedData[0]);
      const rows = formattedData.map((r) => Object.values(r));
      doc.text(t('tireRepairScrap.title', 'Tire Repair & Scrap Report'), 14, 15);
      autoTable(doc, { head: [headers], body: rows, startY: 20 });
    }
    doc.save(`tire-repair-scrap-${dayjs().format('YYYYMMDD')}.pdf`);
  };

  return (
    <Box sx={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <Typography variant="h4" fontWeight="800" gutterBottom color="primary.main">
        {t('tireRepairScrap.title', 'Tire Repair & Scrap')}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0.5, position: 'sticky', top: 0, zIndex: 20, bgcolor: '#f9fafb', pt: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ '& .MuiTab-root': { fontWeight: 'bold', fontSize: '1rem', textTransform: 'none' } }}
        >
          <Tab label={`${t('tireRepairScrap.repairAndScrap', 'Repair & Scrap')} (${filteredData.length || 0})`} />
          <Tab label={`${t('tireRepairScrap.workOrder', 'Work Order')} (${woCount})`} />
          {/* <Tab label={`Scrapped Tires (${scrappedCount})`} /> */}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Toolbar */}
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 0.5 }}>
          <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormControl sx={{ minWidth: 180, width: { xs: '100%', md: 250 }, backgroundColor: 'white' }} size="small">
              <InputLabel id="company-multi-label">{t('general.company', 'Company')}</InputLabel>
              <Select
                labelId="company-multi-label"
                multiple
                value={selectedCompanyValues}
                onChange={handleCompanyChange}
                input={<OutlinedInput id="select-multiple-chip" label={t('general.company', 'Company')} />}
                renderValue={renderCompanyValue}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: { style: { maxHeight: 300 } }
                }}
              >
                <ListSubheader onKeyDown={(e) => e.stopPropagation()} sx={{ bgcolor: 'background.paper', p: 1, zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder={t('tireRepairScrap.searchCompany', 'Search company...')}
                    fullWidth
                    value={companySearchTerm}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                </ListSubheader>
                <MenuItem value="all">
                  <Checkbox
                    checked={companyOptions && companyOptions.length > 0 && selectedCompanyValues.length === companyOptions.length}
                  />
                  <ListItemText primary={t('tireRepairScrap.selectAll', 'Select All')} />
                </MenuItem>
                {filteredCompanyOptions.map((opt) => {
                  const val = String(opt.value);
                  const label = opt.label ?? opt.name ?? String(opt.value);
                  const checked = selectedCompanyValues.includes(val);
                  return (
                    <MenuItem key={val} value={val}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder={t('tireRepairScrap.searchSerialNumber', 'Search Serial Number...')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ width: { xs: '100%', md: 300 }, backgroundColor: 'white', borderRadius: 1 }}
            />
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {selectedTires.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ backgroundColor: 'white', p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<BuildIcon />}
                  onClick={() => setRepairModalOpen(true)}
                  size="small"
                  sx={{ textTransform: 'none', boxShadow: 'none' }}
                >
                  {t('tireRepairScrap.repair', 'Repair')} ({selectedTires.length})
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => setScrapModalOpen(true)}
                  size="small"
                  sx={{ textTransform: 'none', boxShadow: 'none' }}
                >
                  {t('tireRepairScrap.scrap', 'Scrap')} ({selectedTires.length})
                </Button>
              </Stack>
            )}

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportClick}
              sx={{ backgroundColor: 'white', textTransform: 'none' }}
              size="medium"
            >
              {t('tireRepairScrap.export', 'Export')}
            </Button>
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
              <MenuItem onClick={exportToCSV}>CSV</MenuItem>
              <MenuItem onClick={exportToExcel}>Excel</MenuItem>
              <MenuItem onClick={exportToPDF}>PDF</MenuItem>
            </Menu>
          </Stack>
        </Stack>

        {/* Card List Area */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" my={10}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 0.5,
                px: 1,
                backgroundColor: 'white',
                p: 1,
                borderRadius: 2,
                border: '1px solid #eee'
              }}
            >
              <Checkbox
                checked={filteredData.length > 0 && selectedTires.length === filteredData.length}
                indeterminate={selectedTires.length > 0 && selectedTires.length < filteredData.length}
                onChange={handleSelectAll}
                size="small"
              />
              <Typography variant="body2" color="textSecondary" fontWeight="bold">
                {t('tireRepairScrap.selectAllOnThisPage', 'Select All on this page')}
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              {filteredData.length > 0 ? (
                filteredData.map((tire) => {
                  const isSelected = selectedTires.some((t) => t.id === tire.id);
                  return (
                    <Card
                      key={tire.id}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        boxShadow: 'none',
                        transition: 'all 0.15s ease',
                        backgroundColor: isSelected ? '#f4f9ff' : 'white',
                        '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, pr: { xs: 1, md: 0 }, pt: { xs: 1, md: 0 } }}>
                        <Checkbox checked={isSelected} onChange={() => handleSelectOne(tire)} size="small" />
                      </Box>

                      <CardContent sx={{ flex: 1, py: { xs: 1, md: 1.5 }, px: 2, '&:last-child': { pb: { xs: 1, md: 1.5 } } }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                              <Typography variant="subtitle1" fontWeight="800" sx={{ lineHeight: 1 }}>
                                {tire.serial_number}
                              </Typography>
                              <Tooltip title={t('general.showDetail', 'Show Detail')}>
                                <IconButton size="small" onClick={() => setDetailModal({ open: true, data: tire })} sx={{ p: 0.5 }}>
                                  <InfoOutlinedIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                              {tire.status_backlog && (
                                <Chip
                                  label={
                                    tire.status_backlog === 'CRITICAL'
                                      ? `${t('tireRepairScrap.priority', 'Priority')} CRITICAL`
                                      : tire.status_backlog === 'HIGH'
                                        ? `${t('tireRepairScrap.priority', 'Priority')} High`
                                        : tire.status_backlog === 'MEDIUM'
                                          ? `${t('tireRepairScrap.priority', 'Priority')} Medium`
                                          : tire.status_backlog === 'LOW'
                                            ? `${t('tireRepairScrap.priority', 'Priority')} Low`
                                            : tire.status_backlog
                                  }
                                  size="small"
                                  color={
                                    tire.status_backlog === 'CRITICAL' || tire.status_backlog === 'HIGH'
                                      ? 'error'
                                      : tire.status_backlog === 'MEDIUM'
                                        ? 'warning'
                                        : tire.status_backlog === 'LOW'
                                          ? 'success'
                                          : 'default'
                                  }
                                  icon={
                                    tire.status_backlog === 'CRITICAL' || tire.status_backlog === 'HIGH' ? (
                                      <WarningAmberIcon sx={{ fontSize: 14 }} />
                                    ) : undefined
                                  }
                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                />
                              )}
                            </Stack>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {tire.join?.tire_item_tire_brand_name || '-'} •{' '}
                              {tire.join?.tire_item_tire_size_code_internal || tire.join?.tire_item_tire_size_code || '-'}
                            </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center" mt={0.5}>
                              <Tooltip title="Vehicle">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <LocalShippingIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                  <Typography variant="caption" color="primary.main" fontWeight="bold">
                                    {tire.last_vehicle || '-'}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                              <Tooltip title="Position">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="caption" color="primary.main" fontWeight="bold">
                                    {t('detailTireInventory.lastPosition', 'Last Position')}: {tire.last_position || '-'}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            </Stack>
                          </Grid>

                          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                              {t('tireRepairScrap.damageType', 'Damage Type')}
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="body2" fontWeight="600" color="error.dark" sx={{ lineHeight: 1 }}>
                                  {tire.last_inspection?.tire_damage_name || '-'}
                                </Typography>
                                {tire.last_inspection?.tire_damage && getInfoTireDamage(tire.last_inspection?.tire_damage) && (
                                  <Tooltip
                                    title={
                                      <Box sx={{ p: 0.5 }}>
                                        <Typography variant="caption" display="block" color="error.light" sx={{ mb: 0.5 }}>
                                          <strong>{t('tireRepairScrap.cause', 'Cause:')}</strong>{' '}
                                          {getInfoTireDamage(tire.last_inspection.tire_damage)?.cause || '-'}
                                        </Typography>
                                        <Typography variant="caption" display="block" color="info.light">
                                          <strong>{t('tireRepairScrap.area', 'Area:')}</strong>{' '}
                                          {getInfoTireDamage(tire.last_inspection.tire_damage)?.area || '-'}
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                  </Tooltip>
                                )}
                              </Box>
                              {tire.last_inspection?.tire_damage &&
                                getInfoTireDamage(tire.last_inspection?.tire_damage)?.recommend_action && (
                                  <Box>
                                    <Chip
                                      label={getInfoTireDamage(tire.last_inspection.tire_damage).recommend_action}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                    />
                                  </Box>
                                )}
                            </Box>
                          </Grid>

                          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                              {t('tireRepairScrap.patternAndInspector', 'Pattern & Inspector')}
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {tire.join?.m_tire_spec_pattern || tire.m_tire_spec_pattern || '-'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {t('tireRepairScrap.by', 'by')} {tire.last_inspection?.inspector_name || '-'}
                            </Typography>
                          </Grid>

                          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="h5" color="text.secondary" fontWeight="medium">
                                {t('tireRepairScrap.waiting', 'Waiting:')}{' '}
                                <Box component="span" color="warning.dark" fontWeight="bold">
                                  {getWaitingTime(tire.remove_date)}
                                </Box>
                              </Typography>
                            </Stack>
                            <Chip
                              label={`${t('tireRepairScrap.retread', 'Retread')}: ${tire.count_retread}/3`}
                              size="small"
                              color={tire.count_retread >= 3 ? 'error' : 'info'}
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', mt: 1 }}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>

                      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                      <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'row', md: 'column' },
                          justifyContent: 'center',
                          p: 1.5,
                          gap: 1,
                          minWidth: 120
                        }}
                      >
                        {tire.count_retread >= 3 ? (
                          <Tooltip title={t('tireRepairScrap.retread3Times', 'Ban sudah di-retread 3 kali, hanya bisa di-Scrap')}>
                            <span>
                              <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                disabled
                                sx={{ textTransform: 'none', py: 0.2, boxShadow: 'none', width: '100%' }}
                              >
                                {t('tireRepairScrap.repair', 'Repair')}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            onClick={() => {
                              setSelectedTires([tire]);
                              setRepairModalOpen(true);
                            }}
                            sx={{ textTransform: 'none', py: 0.2, boxShadow: 'none' }}
                          >
                            {t('tireRepairScrap.repair', 'Repair')}
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => {
                            setSelectedTires([tire]);
                            setScrapModalOpen(true);
                          }}
                          sx={{ textTransform: 'none', py: 0.2, boxShadow: 'none' }}
                        >
                          {t('tireRepairScrap.scrap', 'Scrap')}
                        </Button>
                      </Box>
                    </Card>
                  );
                })
              ) : (
                <Box p={5} textAlign="center" backgroundColor="white" borderRadius={2} border="1px solid #eee">
                  <Typography color="textSecondary">{t('tireRepairScrap.noDataAvailable', 'No data available.')}</Typography>
                </Box>
              )}
            </Stack>

            {/* Pagination */}
            {totalRecord > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('tireRepairScrap.rowsPerPage', 'Rows per page:')}
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(e.target.value);
                      setPage(1);
                    }}
                    size="small"
                    sx={{ height: 30, '.MuiSelect-select': { py: 0.5 } }}
                  >
                    {[10, 25, 50, 100].map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {`${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, totalRecord)} ${t('tireRepairScrap.of', 'of')} ${totalRecord}`}
                  </Typography>
                </Box>
                <Pagination
                  count={Math.ceil(totalRecord / rowsPerPage)}
                  page={page}
                  onChange={(e, val) => setPage(val)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TabWorkOrder onUpdateTotalCount={setWoCount} refreshTrigger={refreshTrigger} />
      </TabPanel>

      {/* <TabPanel value={tabValue} index={2}>
        <TabScrappedTires onUpdateTotalCount={setScrappedCount} />
      </TabPanel> */}

      {/* Modals */}
      <DetailTireDamage open={detailModal.open} onClose={() => setDetailModal({ open: false, data: null })} data={detailModal.data} />
      <TabTireRepair
        open={repairModalOpen}
        onClose={() => setRepairModalOpen(false)}
        selectedTires={selectedTires}
        onSuccess={() => {
          setRepairModalOpen(false);
          setSelectedTires([]);
          if (refetch) refetch();
          setRefreshTrigger((prev) => prev + 1);
        }}
      />
      <TabTireScrap
        open={scrapModalOpen}
        onClose={() => setScrapModalOpen(false)}
        selectedTires={selectedTires}
        onSuccess={() => {
          setScrapModalOpen(false);
          setSelectedTires([]);
          if (refetch) refetch();
          setRefreshTrigger((prev) => prev + 1);
        }}
      />
    </Box>
  );
}
